/* eslint-disable max-len */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { configTypeMap } from '@app/dev/terminal/components/eventTypeFilter/constants';
import EventTypeFilter from '@app/dev/terminal/components/eventTypeFilter';
import { PreferencesContext } from '@common/contexts/preferences';
import CMSApi from '@app/dev/services/cmsApi';
import Config from '@app/dev/config/main';
import CasinoAndLiveCasinoViewer from '../../components/casinoAndLiveCasinoViewer';

import './style.scss';

export const CasinoWrapper = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const routePath = '/casino';
  const searchParams = new URLSearchParams(location.search);
  const defaultCategoryId = searchParams.get('categoryId')
  const defaultCategoryIdUsed = useRef(false)

  const [searchValue, setSearchValue] = useState('');

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState();
  const [gamesData, setGamesData] = useState({
    list: [],
    total: 0,
  });
  const [providers, setProviders] = useState([]);

  const [offset, setOffset] = useState(0);

  const [selectedProviders, setSelectedProviders] = useState(Config.casino.defaultFilteredProvider);

  const [isLoading, setIsLoading] = useState(false);

  const [{ lang }] = useContext(PreferencesContext);

  const getCategoryId = (id) => (id === 'all' ? null : id);

  const loadProviders = async (id) => {
    const { providers: providersList } = await CMSApi.getCasinoProviders({
      only_providers: 1,
      categories: getCategoryId(id || (categoryId !== 28 && categoryId)),
    });
    const { enabledProvidersList } = Config.casino;

    setProviders(
      enabledProvidersList?.length
        ? providersList.filter((provider) => enabledProvidersList.includes(provider.name))
        : providersList,
    );

    return providersList;
  };

  const loadGames = async ({
    category, newProviders, newSearchValue, isScroll = false,
  } = {}) => {
    if (isLoading) return;
    setIsLoading(true);

    const providersForReq = newProviders || selectedProviders;

    const newOffset = isScroll ? offset + Config.casino.games.loaderStep : 0;

    const paging = {
      offset: newOffset,
      limit: newOffset + Config.casino.games.loaderStep,
    };

    const { games: gamesList, total_count } = await CMSApi.getCasinoGames({
      category: getCategoryId(category || categoryId || (!defaultCategoryIdUsed.current ? defaultCategoryId : null)),
      provider: providersForReq.length ? providersForReq.join(',') : null,
      search: newSearchValue || searchValue,
      ...paging,
    });

    defaultCategoryIdUsed.current = true
    setIsLoading(false);
    setOffset(newOffset);
    setGamesData({
      total: +total_count,
      list: isScroll ? gamesData.list.concat(gamesList) : gamesList,
    });
  };

  const loadCategories = async () => {
    const data = await CMSApi.getCasinoCategories({
      only_categories: 1,
    });
  
    if (defaultCategoryId && data.categories.find(item => +item.id === +defaultCategoryId)) setCategoryId(defaultCategoryId)

    await loadProviders();
    setCategories(data?.categories?.filter((item) => item.id !== 28) || []);
  };

  useEffect(() => {
    if (!Config.casino.enabledProvidersList?.length) {
      loadCategories();
    } else loadProviders(); // in case of existing custom providers list, categories doesn't show
  }, []);

  useEffect(() => {
    loadCategories();
  }, [lang]);

  const getProvidersForFilter = (loadedProviders, selectedProvidersList = selectedProviders) => {
    const loadedProvidersNames = new Set(loadedProviders?.map((item) => item.name));

    return selectedProvidersList.filter((item) => loadedProvidersNames.has(item));
  };

  const selectCategory = async (id) => {
    if (gameId) navigate(routePath);
    setCategoryId(id);
    setGamesData();

    if (id === 'lastPlayed') return;

    const loadedProviders = await loadProviders(id);

    loadGames({
      category: id,
      newProviders: getProvidersForFilter(loadedProviders),
    });
  };

  const toggleProvider = (provider) => {
    if (gameId) navigate(routePath);

    let newProviders = selectedProviders;

    if (selectedProviders.find((item) => item === provider)) {
      newProviders = selectedProviders.filter((item) => item !== provider);
    } else {
      newProviders = selectedProviders.concat(provider);
    }

    setSelectedProviders(newProviders);
    loadGames({
      category: categoryId,
      newProviders: getProvidersForFilter(providers, newProviders),
    });
  };

  useEffect(() => {
    loadGames({
      newProviders: Config.casino.enabledProvidersList.length ? Config.casino.enabledProvidersList : null,
    });
  }, [searchValue]);

  useEffect(() => {
    loadGames();
  }, [lang, searchValue]);

  const onSearchChange = (value) => {
    if (value.length >= 3) setSearchValue(value);
    else setSearchValue('');
  };

  return (
    <div className="import-view-container with-type-filter casino-view">
      <EventTypeFilter
        searchText={searchValue}
        setSearchText={setSearchValue}
        listType="casino"
        configType={configTypeMap.casino}
      />
      <CasinoAndLiveCasinoViewer
        selectCategory={selectCategory}
        categories={categories}
        categoryId={categoryId}
        selectedProviders={selectedProviders}
        toggleProvider={toggleProvider}
        providers={providers}
        gamesData={gamesData}
        gameId={gameId}
        loadGames={loadGames}
        onSearchChange={onSearchChange}
        searchValue={searchValue}
        routePath={routePath}
      />
    </div>
  );
};
